import React from 'react';
import { Badge, Row, Col } from 'reactstrap';
import { KycFormQuestion } from '../models/kycModel';

const baseTextStyle = { fontSize: '1rem', fontWeight: 400 };

interface RenderQuestionProps {
  question: KycFormQuestion;
}

const RenderQuestion: React.FC<RenderQuestionProps> = ({ question }) => {
  switch (question.type) {
    case 'text':
    case 'datetime':
    case 'document':
      return (
        <tr key={question.name}>
          <td
            style={{
              ...baseTextStyle,
              maxWidth: 220,
              minWidth: 120,
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
            }}
          >
            {question.label}
          </td>
          <td style={baseTextStyle}>
            {question.value ?? <Badge color="secondary">Sin respuesta</Badge>}
          </td>
        </tr>
      );
    case 'select':
      return (
        <tr key={question.name}>
          <td
            style={{
              ...baseTextStyle,
              maxWidth: 220,
              minWidth: 120,
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
            }}
          >
            {question.label}
          </td>
          <td style={baseTextStyle}>
            {question.value ? (
              question.options?.find((opt: any) => opt.value === question.value)
                ?.label || question.value
            ) : (
              <Badge color="secondary">Sin respuesta</Badge>
            )}
          </td>
        </tr>
      );
    case 'checkbox':
      return (
        <tr key={question.name}>
          <td
            style={{
              ...baseTextStyle,
              maxWidth: 220,
              minWidth: 120,
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
            }}
          >
            {question.label}
          </td>
          <td style={baseTextStyle}>
            {question.value === true ? (
              <Badge color="success">Aceptado</Badge>
            ) : (
              <Badge color="secondary">No aceptado</Badge>
            )}
          </td>
        </tr>
      );
    case 'group':
      // Renderiza cada grupo de valores como una tabla interna
      return (
        <tr key={question.name}>
          <td colSpan={2} style={{ padding: 0 }}>
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  marginBottom: 8,
                }}
              >
                {question.label}
              </div>
              {Array.isArray(question.value) && question.value.length > 0 ? (
                question.value.map((groupItem: any, idx: number) => (
                  <table
                    key={idx}
                    className="table table-bordered"
                    style={{
                      marginBottom: 16,
                      background: '#f8f9fa',
                      borderRadius: 8,
                      overflow: 'hidden',
                    }}
                  >
                    <tbody>
                      {Array.isArray(question.questions) &&
                        question.questions.map((subQ: any) => {
                          // Busca el valor correspondiente en el grupo
                          const value = groupItem[subQ.name];
                          let displayValue = value;
                          // Si es select, busca el label
                          if (subQ.type === 'select' && subQ.options) {
                            const found = subQ.options.find(
                              (opt: any) => opt.value === value,
                            );
                            displayValue = found ? found.label : value;
                          }
                          // Si es checkbox
                          if (subQ.type === 'checkbox') {
                            displayValue =
                              value === true ? (
                                <Badge color="success">Aceptado</Badge>
                              ) : (
                                <Badge color="secondary">No aceptado</Badge>
                              );
                          }
                          // Si es email, text, etc.
                          if (
                            ['text', 'email', 'document', 'datetime'].includes(
                              subQ.type,
                            )
                          ) {
                            displayValue = value ?? (
                              <Badge color="secondary">Sin respuesta</Badge>
                            );
                          }
                          return (
                            <tr key={subQ.name}>
                              <td
                                style={{
                                  ...baseTextStyle,
                                  maxWidth: 220,
                                  minWidth: 120,
                                  wordBreak: 'break-word',
                                  whiteSpace: 'pre-line',
                                  background: '#f1f3f4',
                                }}
                              >
                                {subQ.label}
                              </td>
                              <td style={baseTextStyle}>{displayValue}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                ))
              ) : (
                <Badge color="secondary">Sin datos</Badge>
              )}
            </div>
          </td>
        </tr>
      );
    case 'image':
      return (
        <tr key={question.name}>
          <td
            style={{
              ...baseTextStyle,
              maxWidth: 220,
              minWidth: 120,
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
            }}
          >
            {question.label}
          </td>
          <td style={baseTextStyle}>
            {Array.isArray(question.value) && question.value.length > 0 ? (
              <Row>
                {question.value.map((fileUrl: string, idx: number) => (
                  <Col
                    key={idx}
                    md={4}
                    className="mb-2 d-flex flex-column align-items-center"
                  >
                    <Badge color="info" className="mb-2" style={{ fontSize: 13 }}>
                      Archivo {idx + 1}
                    </Badge>
                    <button
                      type="button"
                      style={{
                        border: '1px solid #007bff',
                        background: 'transparent',
                        color: '#007bff',
                        borderRadius: 6,
                        padding: '4px 14px',
                        fontSize: 14,
                        marginTop: 2,
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onClick={() =>
                        window.open(fileUrl, '_blank', 'noopener,noreferrer')
                      }
                    >
                      Ver archivo
                    </button>
                  </Col>
                ))}
              </Row>
            ) : (
              <Badge color="secondary">Sin archivos</Badge>
            )}
          </td>
        </tr>
      );
    default:
      return (
        <tr key={question.name}>
          <td
            style={{
              ...baseTextStyle,
              maxWidth: 220,
              minWidth: 120,
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
            }}
          >
            {question.label}
          </td>
          <td style={baseTextStyle}>
            {question.value ?? <Badge color="secondary">Sin respuesta</Badge>}
          </td>
        </tr>
      );
  }
};

export default RenderQuestion;
